import * as React from 'react'
import Card from '@mui/material/Card'
import Box from '@mui/material/Box'
import CardActions from '@mui/material/CardActions'
import CardContent from '@mui/material/CardContent'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import Tooltip from '@mui/material/Tooltip'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import GppGoodIcon from '@mui/icons-material/GppGood'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useDate } from 'utils/useDate'

type VendorRfpCardSentProps = {
  proposal: AvailableToModifyRequestForProposals
}

const AvailableToEditProposalCard: React.FC<VendorRfpCardSentProps> = ({ proposal }) => {
  const { t } = useTranslation()
  const { formatDate } = useDate()

  // Use the actual timestamp and format it ourselves with "klo" between date and time
  const submittedTimestamp = proposal.proposalLastUpdatedTS ?? proposal.proposalLeftTS
  const formattedDateTime = submittedTimestamp 
    ? `${formatDate(submittedTimestamp, 'd.M.yyyy')} ${t('at-time')} ${formatDate(submittedTimestamp, 'HH:mm')}`
    : proposal.proposalLastUpdatedDisplay ?? proposal.proposalLeftDisplay

  const isLocked = proposal.rfpProposalViewingLockedUntilDueDate === true

  return (
    <Card sx={{ border: '1px solid #E5E7EB', position: 'relative', overflow: 'visible' }}>
      {isLocked && (
        <Tooltip title={t('request-for-proposal-proposal-viewing-locked')}>
          <Box
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              zIndex: 10,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <GppGoodIcon sx={{ color: 'primary.main', fontSize: 24 }} />
          </Box>
        </Tooltip>
      )}
      <CardContent
        sx={{
          position: 'relative',
          ...(isLocked && {
            pr: { xs: 7, sm: 3 }
          })
        }}
      >
        <Typography sx={{ fontSize: 14 }} color="text.secondary">
          {`${proposal.buyerName} (${proposal.tenantName})`}
        </Typography>
        <Typography sx={{ fontSize: 16, fontWeight: 'fontWeightMedium', mb: 1 }}>{proposal.rfpTitle}</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <CheckCircleIcon sx={{ mr: 1 }} color="success" />
          <Typography sx={{ fontSize: 14 }}>{`${t('proposal-submitted')} ${formattedDateTime}`}</Typography>
        </Box>
      </CardContent>
      <CardActions>
        <Box sx={{ display: 'flex', flex: 1, alignItems: 'center', justifyContent: 'space-between', ml: 1, mb: 1 }}>
          <Button
            component={Link}
            to={`/${proposal.proposalId}/edit`}
            state={{ from: '/vendors' }}
            color="grey"
            variant="outlined"
          >
            {t('edit')}
          </Button>
          <Typography sx={{ mr: 1 }} variant="caption" color="text.secondary">
            {t('due-date-is')} {proposal.proposalDueDateDisplay}
          </Typography>
        </Box>
      </CardActions>
    </Card>
  )
}

export default AvailableToEditProposalCard
